/**
 * FeedContext — manages shared state for filters, language preference, and saved items.
 *
 * Does NOT fetch items. MainFeed handles its own data fetching directly.
 * This eliminates duplicate concurrent Lambda calls that were doubling cold start load.
 */
import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import type { FeedContextType, FeedFilters, NewsItem } from '@/types/feed'
import { DEFAULT_FILTERS } from '@/types/feed'
import { getApiClient } from '@/services/api'

const FeedContext = createContext<FeedContextType | undefined>(undefined)

interface FeedProviderProps {
  children: ReactNode
}


export function FeedProvider({ children }: FeedProviderProps) {
  const [filters, setFiltersState] = useState<FeedFilters>(DEFAULT_FILTERS)

  // Sources map: source_id → human-readable name (fetched once, no auth required)
  const [sourcesMap, setSourcesMap] = useState<Record<string, string>>({})
  useEffect(() => {
    getApiClient()
      .get<{ source_id: string; name: string }[]>('/api/v1/sources')
      .then((resp) => {
        const map: Record<string, string> = {}
        for (const s of resp.data ?? []) {
          if (s.source_id && s.name) map[s.source_id] = s.name
        }
        setSourcesMap(map)
      })
      .catch((err) => console.warn('Could not load sources map:', err))
  }, [])

  // Language preference — hardcoded to Hebrew (this is a Hebrew news site)
  const lang = 'he'

  /** Set language — no-op for now (Hebrew only) */
  const setLang = useCallback((_newLang: string) => {
    // Hebrew-only site — no language switching
  }, [])

  // Saved items — initialize synchronously from localStorage
  const [savedItemIds, setSavedItemIds] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('savedNewsItems')
      return saved ? new Set(JSON.parse(saved)) : new Set()
    } catch {
      return new Set()
    }
  })



  /** Update filters — MainFeed watches filters via context and re-fetches on change */
  const setFilters = useCallback((newFilters: Partial<FeedFilters> | ((prev: FeedFilters) => FeedFilters)) => {
    if (typeof newFilters === 'function') {
      setFiltersState(newFilters)
    } else {
      setFiltersState(prev => ({ ...prev, ...newFilters }))
    }
  }, [])

  const clearFilters = useCallback(() => {
    setFiltersState(DEFAULT_FILTERS)
  }, [])

  /** Save item to personal list */
  const saveItem = useCallback(async (itemId: string) => {
    setSavedItemIds(prev => {
      const updated = new Set(prev)
      updated.add(itemId)
      localStorage.setItem('savedNewsItems', JSON.stringify(Array.from(updated)))
      return updated
    })
  }, [])

  /** Remove item from personal list */
  const unsaveItem = useCallback(async (itemId: string) => {
    setSavedItemIds(prev => {
      const updated = new Set(prev)
      updated.delete(itemId)
      localStorage.setItem('savedNewsItems', JSON.stringify(Array.from(updated)))
      return updated
    })
  }, [])

  const value: FeedContextType = {
    // Items state — empty stubs; MainFeed manages its own item state
    items: [] as NewsItem[],
    isLoading: false,
    error: null,
    hasMore: true,
    page: 1,
    loadMore: () => {},
    refresh: () => {},

    filters,
    setFilters,
    clearFilters,
    lang,
    setLang,
    savedItemIds,
    saveItem,
    unsaveItem,
    sourcesMap,
  }

  return <FeedContext.Provider value={value}>{children}</FeedContext.Provider>
}

export function useFeedContext(): FeedContextType {
  const context = useContext(FeedContext)
  if (!context) {
    throw new Error('useFeedContext must be used within FeedProvider')
  }
  return context
}
